import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  Avatar,
  Divider,
  CircularProgress,
  LinearProgress,
  Paper,
  Tooltip,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  customUsersAPI,
  rolesAPI,
  adminAPI,
  permissionsAPI,
} from "../../shared/services/api";
import * as XLSX from "xlsx";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import {
  People as PeopleIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AdminPanelSettings as AdminIcon,
  Assignment as PermissionIcon,
} from "@mui/icons-material";
import { useAuth } from "../../shared/contexts/AuthContext";
import Cookies from "js-cookie";
import toast from "react-hot-toast";

export default function AdminDashboard() {
  const { user, token, isAuthenticated } = useAuth();

  // Debug authentication
  console.log("Auth Debug Info:", {
    isAuthenticated,
    user: user?.name || "No user",
    token: token ? "Present" : "Missing",
    cookieToken: Cookies.get("token") ? "Present" : "Missing",
    localStorageToken: localStorage.getItem("token") ? "Present" : "Missing",
  });

  // Fetch dashboard statistics from admin API
  const {
    data: dashboardData,
    isLoading: adminLoading,
    error: adminError,
  } = useQuery({
    queryKey: ["admin", "dashboard"],
    queryFn: () => adminAPI.getDashboardStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Always fetch users data as fallback/verification
  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
  } = useQuery({
    queryKey: ["customUsers", "dashboard"],
    queryFn: () => customUsersAPI.getCustomUsers({ limit: 1000 }),
    refetchInterval: 30000,
  });

  // Fetch roles data
  const {
    data: rolesData,
    isLoading: rolesLoading,
    error: rolesError,
  } = useQuery({
    queryKey: ["roles"],
    queryFn: () => rolesAPI.getRoles(),
    refetchInterval: 30000,
  });

  // Fetch permissions data
  const {
    data: permissionsData,
    isLoading: permissionsLoading,
    error: permissionsError,
  } = useQuery({
    queryKey: ["permissions"],
    queryFn: () => permissionsAPI.getPermissions(),
    refetchInterval: 30000,
  });

  const stats = dashboardData?.data;
  const users = usersData?.data?.data || [];
  const roles = rolesData?.data?.data || [];
  const permissions = permissionsData?.data?.data || [];
  const statsLoading =
    adminLoading || usersLoading || rolesLoading || permissionsLoading;
  const statsError = adminError || usersError || rolesError || permissionsError;

  // Debug logging
  console.log("Dashboard Debug Info:", {
    adminLoading,
    usersLoading,
    rolesLoading,
    permissionsLoading,
    adminError: adminError?.message || adminError,
    usersError: usersError?.message || usersError,
    rolesError: rolesError?.message || rolesError,
    permissionsError: permissionsError?.message || permissionsError,
    adminErrorResponse: adminError?.response?.data,
    usersErrorResponse: usersError?.response?.data,
    rolesErrorResponse: rolesError?.response?.data,
    permissionsErrorResponse: permissionsError?.response?.data,
    stats,
    usersDataStructure: usersData,
    rolesDataStructure: rolesData,
    permissionsDataStructure: permissionsData,
    usersCount: users.length,
    rolesCount: roles.length,
    permissionsCount: permissions.length,
    dashboardDataRaw: dashboardData,
    usersDataRaw: usersData,
    rolesDataRaw: rolesData,
    permissionsDataRaw: permissionsData,
  });

  // Calculate total permissions from hierarchical permission modules
  const calculateTotalPermissions = (permissionModules) => {
    let totalSections = 0;
    permissionModules.forEach((module) => {
      module.features?.forEach((feature) => {
        feature.subFeatures?.forEach((subFeature) => {
          totalSections += subFeature.sections?.length || 0;
        });
      });
    });
    return totalSections;
  };

  // Excel export function
  const exportUsersToExcel = async (userType) => {
    try {
      // Fetch users for the specific portal type
      const response = await customUsersAPI.getCustomUsers({
        userType: userType,
        limit: 1000,
      });

      const usersToExport = response.data?.data || [];

      if (usersToExport.length === 0) {
        toast.error(`No users found for ${userType} portal`);
        return;
      }

      // Prepare data for Excel export
      const excelData = usersToExport.map((user) => ({
        "eOxegen ID": user.eoxegenId || "",
        Name: user.name || "",
        "User Type": user.userType || "",
        Status: user.status || "",
        "Corporate ID": user.corporateId || "",
        "Corporate Name": user.corporateName || "",
        "Intermediary ID": user.intermediaryId || "",
        "Intermediary Name": user.intermediaryName || "",
        "Insurer ID": user.insurerId || "",
        "Insurer Name": user.insurerName || "",
        "Provider ID": user.providerId || "",
        "Provider Name": user.providerName || "",
        "Member Type": user.memberType || "",
        Roles: user.roles?.map((role) => role.name || role).join(", ") || "",
        "Created At": user.createdAt
          ? new Date(user.createdAt).toLocaleDateString()
          : "",
        "Updated At": user.updatedAt
          ? new Date(user.updatedAt).toLocaleDateString()
          : "",
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Add the worksheet to workbook
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        `${userType.charAt(0).toUpperCase() + userType.slice(1)} Users`
      );

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `${userType}_portal_users_${timestamp}.xlsx`;

      // Save the file
      XLSX.writeFile(wb, filename);

      toast.success(
        `Exported ${usersToExport.length} ${userType} portal users to ${filename}`
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error(
        `Failed to export ${userType} portal users: ${error.message}`
      );
    }
  };

  // Calculate dashboard statistics from API data or fallback
  let dashboardStats;

  // Calculate real permissions count
  const realPermissionsCount =
    permissions.length > 0 ? calculateTotalPermissions(permissions) : 0;

  // Prefer admin API data if available and has meaningful data, otherwise use fallback
  if (
    stats &&
    !adminError &&
    (stats.users?.total > 0 || stats.roles?.total > 0)
  ) {
    // Use admin API data but exclude admin users
    const usersByTypeFiltered =
      stats.users?.byType?.reduce((acc, item) => {
        if (item._id !== "admin") {
          // Exclude admin users
          acc[item._id] = item.count;
        }
        return acc;
      }, {}) || {};

    // Calculate total users excluding admin
    const totalUsersExcludingAdmin = Object.values(usersByTypeFiltered).reduce(
      (sum, count) => sum + count,
      0
    );

    dashboardStats = {
      totalUsers: totalUsersExcludingAdmin,
      totalRoles: stats.roles?.total || 0,
      totalPermissions: stats.permissions?.total || realPermissionsCount || 94, // Use admin API count, then real count, then fallback
      activeUsers: stats.users?.active || 0, // Note: This still includes admin users in active count
      usersByType: usersByTypeFiltered,
      recentUsers: stats.users?.recentSignups || 0,
    };
    console.log(
      "Using admin API data (excluding admin users):",
      dashboardStats
    );
  } else {
    // Use fallback user data but exclude admin users
    const nonAdminUsers = users.filter((user) => user.userType !== "admin");
    const totalUsersFromAPI = nonAdminUsers.length;
    const allPortalTypes = [
      "member",
      "corporate",
      "provider",
      "intermediary",
      "insurer",
      "irda",
    ]; // Exclude admin
    const usersByType = allPortalTypes.reduce((acc, type) => {
      acc[type] = nonAdminUsers.filter((user) => user.userType === type).length;
      return acc;
    }, {});

    // Calculate recent users (last 7 days) for the chip display, excluding admin users
    const recentUsersCount = nonAdminUsers.filter((user) => {
      const createdDate = new Date(user.createdAt);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return createdDate > weekAgo;
    }).length;

    dashboardStats = {
      totalUsers: totalUsersFromAPI,
      totalRoles: roles.length || 0,
      totalPermissions:
        realPermissionsCount ||
        roles.reduce((acc, role) => acc + (role.permissions?.length || 0), 0) ||
        94,
      activeUsers: nonAdminUsers.filter((user) => user.status === "active")
        .length,
      usersByType: usersByType,
      recentUsers: recentUsersCount,
    };
    console.log("Using fallback data:", dashboardStats);
  }

  // Mock login data for the graph
  const mockLoginData = [
    { date: "2024-01-01", logins: 45 },
    { date: "2024-01-02", logins: 52 },
    { date: "2024-01-03", logins: 38 },
    { date: "2024-01-04", logins: 61 },
    { date: "2024-01-05", logins: 55 },
    { date: "2024-01-06", logins: 42 },
    { date: "2024-01-07", logins: 67 },
    { date: "2024-01-08", logins: 58 },
    { date: "2024-01-09", logins: 49 },
    { date: "2024-01-10", logins: 73 },
    { date: "2024-01-11", logins: 65 },
    { date: "2024-01-12", logins: 51 },
    { date: "2024-01-13", logins: 44 },
    { date: "2024-01-14", logins: 69 },
  ];

  const systemHealth = {
    status: "healthy",
    uptime: "99.9%",
    lastCheck: new Date().toISOString(),
  };

  const StatCard = ({ title, value, icon, color = "primary", subtitle }) => (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: `${color}.main`,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {" "}
        {/* Reduced from 3 */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h4" // Changed from h3
              component="div"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                mb: 0.25, // Reduced from 0.5
                fontFamily:
                  '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: "1.5rem", // More compact
              }}
            >
              {statsLoading ? "-" : value}
            </Typography>
            <Typography
              variant="body2" // Changed from body1
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                fontFamily:
                  '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: "0.8125rem", // More compact
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.disabled",
                  mt: 0.5,
                  fontFamily:
                    '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: `#e0e0e0`,
              color: `${color}.main`,
              width: 48,
              height: 48,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const getHealthStatusColor = (status) => {
    switch (status) {
      case "healthy":
        return "success";
      case "degraded":
        return "warning";
      case "unhealthy":
        return "error";
      default:
        return "success";
    }
  };

  const getUserTypeColor = (userType) => {
    const colors = {
      admin: "#ef4444",
      member: "#3b82f6",
      corporate: "#10b981",
      provider: "#f59e0b",
      intermediary: "#8b5cf6",
      insurer: "#6b7280",
      irda: "#374151",
    };
    return colors[userType] || "#6b7280";
  };

  const getHealthStatusIcon = (status) => {
    switch (status) {
      case "healthy":
        return <CheckCircleIcon />;
      case "degraded":
        return <WarningIcon />;
      case "unhealthy":
        return <ErrorIcon />;
      default:
        return <CheckCircleIcon />;
    }
  };

  if (statsError) {
    console.error("Dashboard Error Details:", {
      usersError: usersError,
      rolesError: rolesError,
      usersErrorMessage: usersError?.message,
      rolesErrorMessage: rolesError?.message,
      usersErrorResponse: usersError?.response?.data,
      rolesErrorResponse: rolesError?.response?.data,
    });

    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load dashboard data: {statsError.message}
        <br />
        Users Error:{" "}
        {usersError?.response?.data?.message || usersError?.message || "None"}
        <br />
        Roles Error:{" "}
        {rolesError?.response?.data?.message || rolesError?.message || "None"}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        // width: '75vw',
        fontFamily:
          '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <Box sx={{ mb: 3 }}>
        {" "}
        {/* Reduced from 4 */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            color: "#1a1a1a",
            mb: 0.5, // Reduced from 1
            fontFamily:
              '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: "1.5rem", // More compact
          }}
        >
          Admin Dashboard
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#6b7280",
            fontFamily:
              '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: "14px", // Reduced from 16px
          }}
        >
          System overview and key metrics
        </Typography>
        {adminError && (
          <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
            Using fallback data - Admin API may be unavailable
          </Alert>
        )}
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {" "}
        {/* Reduced spacing and margin */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Users"
            value={dashboardStats.totalUsers}
            icon={<PeopleIcon />}
            color="primary"
            subtitle={`${dashboardStats.activeUsers} active users`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Active Roles"
            value={dashboardStats.totalRoles}
            icon={<SecurityIcon />}
            color="secondary"
            subtitle="System and custom roles"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Permissions"
            value={dashboardStats.totalPermissions}
            icon={<AdminIcon />}
            color="success"
            subtitle="Permission modules"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Recent Logins"
            value={mockLoginData[mockLoginData.length - 1]?.logins || 0}
            icon={<CheckCircleIcon />}
            color="info"
            subtitle="Today's login count"
          />
        </Grid>
      </Grid>

      {/* Additional Information Cards */}
      <Grid container spacing={3}>
        {/* Login Activity Graph */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 3,
              bgcolor: "white",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: "#1a1a1a",
                  fontFamily:
                    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
              >
                Login Activity (Last 14 Days)
              </Typography>

              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mockLoginData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })
                      }
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      }
                      formatter={(value) => [value, "Logins"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="logins"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: "#3b82f6", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        {/* All Users by Portal Type */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              border: "1px solid #e5e7eb",
              borderRadius: 3,
              bgcolor: "white",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: "#1a1a1a",
                  fontFamily:
                    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
              >
                All Users by Portal Type
              </Typography>

              {statsLoading ? (
                <LinearProgress sx={{ mb: 2 }} />
              ) : (
                <Box sx={{ maxHeight: 350, overflow: "auto" }}>
                  <List>
                    {Object.entries(dashboardStats.usersByType).map(
                      ([userType, count]) => (
                        <ListItem key={userType} sx={{ px: 0, py: 1.5 }}>
                          <ListItemIcon>
                            <PeopleIcon
                              sx={{ color: getUserTypeColor(userType) }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${
                              userType.charAt(0).toUpperCase() +
                              userType.slice(1)
                            } Portal`}
                            secondary={`${count} user${
                              count !== 1 ? "s" : ""
                            } registered`}
                            primaryTypographyProps={{
                              fontFamily:
                                '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                              fontWeight: 500,
                              color: "#1a1a1a",
                            }}
                            secondaryTypographyProps={{
                              fontFamily:
                                '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                              color: "#6b7280",
                            }}
                          />
                          <Box sx={{ marginRight: 2 }}>
                            <Tooltip
                              title={`Click to export ${userType} portal users to Excel`}
                              arrow
                            >
                              <Chip
                                label={count}
                                size="small"
                                clickable
                                onClick={() => exportUsersToExcel(userType)}
                                sx={{
                                  bgcolor: getUserTypeColor(userType),
                                  color: "white",
                                  fontWeight: 500,
                                  cursor: "pointer",
                                  "&:hover": {
                                    opacity: 0.8,
                                    transform: "scale(1.05)",
                                  },
                                }}
                              />
                            </Tooltip>
                          </Box>
                        </ListItem>
                      )
                    )}

                    {Object.keys(dashboardStats.usersByType).length === 0 && (
                      <ListItem sx={{ px: 0, py: 2 }}>
                        <ListItemText
                          primary="No users found"
                          secondary="Users will appear here once they are created"
                          primaryTypographyProps={{
                            fontFamily:
                              '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            fontWeight: 500,
                            color: "#6b7280",
                          }}
                          secondaryTypographyProps={{
                            fontFamily:
                              '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            color: "#9ca3af",
                          }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#6b7280",
                    fontFamily:
                      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  }}
                >
                  All Time Statistics
                </Typography>
                <Chip
                  label={`${dashboardStats.totalUsers} total users`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
